<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextInput from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  multiline: { type: Boolean, default: false },
  credentialFields: { type: Array, default: () => [] },
  contactCustomAttributes: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const fieldRef = ref(null);
const activeInputValue = ref(props.modelValue);
const cursor = ref(0);
const selectedIndex = ref(0);
const isOpen = ref(false);

const roots = computed(() => [
  {
    value: 'contact.',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.CONTACT'),
    example: '{{contact.email}}',
  },
  {
    value: 'conversation.',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.CONVERSATION'),
    example: '{{conversation.id}}',
  },
  {
    value: 'arguments.',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.ARGUMENTS'),
    example: '{{arguments.valor}}',
  },
  {
    value: 'credential.',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.CREDENTIAL'),
    example: '{{credential.base_url}}',
  },
]);

const fieldsByNamespace = computed(() => ({
  contact: [
    'id',
    'name',
    'email',
    'phone_number',
    ...props.contactCustomAttributes,
  ],
  conversation: ['id', 'inbox_id', 'contact_id', 'account_id'],
  arguments: ['valor'],
  credential: ['base_url', ...props.credentialFields],
}));

const transforms = computed(() => [
  {
    value: 'digitsOnly()',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.DIGITS_ONLY'),
    example: '{{contact.phone_number.digitsOnly()}}',
  },
  {
    value: 'trim()',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.TRIM'),
    example: '{{contact.name.trim()}}',
  },
  {
    value: 'toLowerCase()',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.TO_LOWER_CASE'),
    example: '{{contact.email.toLowerCase()}}',
  },
  {
    value: 'toUpperCase()',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.TO_UPPER_CASE'),
    example: '{{contact.name.toUpperCase()}}',
  },
  {
    value: "replace('texto', '')",
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.REPLACE'),
    example: "{{contact.server_url.replace('https://', '')}}",
  },
  {
    value: 'removeWhitespace()',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.REMOVE_WHITESPACE'),
    example: '{{contact.phone_number.removeWhitespace()}}',
  },
  {
    value: 'normalizeWhitespace()',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.NORMALIZE_WHITESPACE'),
    example: '{{contact.name.normalizeWhitespace()}}',
  },
  {
    value: 'slice(0, 8)',
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.SLICE'),
    example: '{{contact.phone_number.slice(0, 8)}}',
  },
  {
    value: "split(',')",
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.SPLIT'),
    example: "{{contact.tags.split(',')}}",
  },
  {
    value: "join(', ')",
    label: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.JOIN'),
    example: "{{contact.tags.split(',').join(', ')}}",
  },
]);

const inputElement = () =>
  fieldRef.value?.$el?.querySelector(props.multiline ? 'textarea' : 'input');

const currentExpression = computed(() => {
  const start = activeInputValue.value.lastIndexOf('{{', cursor.value - 1);
  if (start === -1) return null;

  const source = activeInputValue.value.slice(start + 2, cursor.value);
  return source.includes('}}') ? null : { start, source };
});

const suggestions = computed(() => {
  const expression = currentExpression.value;
  if (!expression) return [];

  const source = expression.source.trim();
  if (!source.includes('.')) {
    return roots.value
      .filter(option => option.value.startsWith(source))
      .map(option => ({
        ...option,
        replacement: option.value,
        complete: false,
      }));
  }

  const [namespace, ...parts] = source.split('.');
  const tail = parts.at(-1);
  const prefix = `${namespace}.${parts.slice(0, -1).join('.')}`;
  if (parts.length === 1) {
    return (fieldsByNamespace.value[namespace] || [])
      .map(field =>
        typeof field === 'string' ? { value: field, label: field } : field
      )
      .filter(field => field.value.startsWith(tail))
      .map(field => ({
        ...field,
        replacement: `${namespace}.${field.value}`,
      }));
  }

  if (parts.length >= 2) {
    const selectedTransforms = source.includes('.split(')
      ? transforms.value.filter(transform => transform.value !== "split(',')")
      : transforms.value.filter(transform => transform.value !== "join(', ')");

    return selectedTransforms
      .filter(transform => transform.value.startsWith(tail))
      .map(transform => ({
        ...transform,
        replacement: `${prefix}${prefix.endsWith('.') ? '' : '.'}${transform.value}`,
      }));
  }

  return [];
});

const refreshSuggestionsAtCursor = () => {
  selectedIndex.value = 0;
  isOpen.value = suggestions.value.length > 0;
};

const refreshSuggestions = event => {
  cursor.value = event.target.selectionStart ?? event.target.value.length;
  refreshSuggestionsAtCursor();
};

const updateValue = value => {
  activeInputValue.value = value;
  emit('update:modelValue', value);
};

watch(
  () => props.modelValue,
  value => {
    activeInputValue.value = value;
  }
);

const applySuggestion = option => {
  const expression = currentExpression.value;
  if (!expression) return;

  const before = activeInputValue.value.slice(0, expression.start + 2);
  const after = activeInputValue.value.slice(cursor.value);
  const closingBraces =
    option.complete !== false && !after.startsWith('}}') ? '}}' : '';
  const nextValue = `${before}${option.replacement}${closingBraces}${after}`;
  const nextCursor = before.length + option.replacement.length;
  activeInputValue.value = nextValue;
  emit('update:modelValue', nextValue);
  isOpen.value = option.complete === false;

  nextTick(() => {
    const input = inputElement();
    input?.focus();
    input?.setSelectionRange(nextCursor, nextCursor);
    cursor.value = nextCursor;
    if (option.complete === false) refreshSuggestionsAtCursor();
  });
};

const handleKeydown = event => {
  if (!isOpen.value || !suggestions.value.length) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    selectedIndex.value = (selectedIndex.value + 1) % suggestions.value.length;
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    selectedIndex.value =
      (selectedIndex.value - 1 + suggestions.value.length) %
      suggestions.value.length;
  } else if (event.key === 'Enter' || event.key === 'Tab') {
    event.preventDefault();
    applySuggestion(suggestions.value[selectedIndex.value]);
  } else if (event.key === 'Escape') {
    event.preventDefault();
    isOpen.value = false;
  }
};
</script>

<template>
  <div class="relative">
    <component
      :is="multiline ? TextArea : NextInput"
      ref="fieldRef"
      v-bind="$attrs"
      :model-value="modelValue"
      :placeholder="placeholder"
      @update:model-value="updateValue"
      @input="refreshSuggestions"
      @focus="refreshSuggestions"
      @keydown.capture="handleKeydown"
      @blur="isOpen = false"
    />
    <div
      v-if="isOpen && suggestions.length"
      role="listbox"
      :aria-label="t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.LABEL')"
      class="absolute z-50 mt-1 max-h-52 w-full overflow-y-auto rounded-lg border border-n-weak bg-n-solid-1 p-1 shadow-lg"
    >
      <NextButton
        v-for="(option, index) in suggestions"
        :key="option.replacement"
        v-tooltip.right="{
          content: t('CORTEX.TOOLS.FORM.AUTOCOMPLETE.EXAMPLE', {
            example: option.example || option.replacement,
          }),
          delay: { show: 250, hide: 0 },
        }"
        type="button"
        role="option"
        :aria-selected="selectedIndex === index"
        slate
        ghost
        no-animation
        class="h-auto w-full justify-between rounded-md px-3 py-2 text-left"
        :class="
          selectedIndex === index
            ? 'bg-n-brand/10 text-n-brand'
            : 'hover:bg-n-alpha-2'
        "
        @mousedown.prevent="applySuggestion(option)"
      >
        <code class="font-mono text-xs">{{ option.replacement }}</code>
        <span class="text-xs text-n-slate-11">{{ option.label }}</span>
      </NextButton>
    </div>
  </div>
</template>
